import React from "react";
import { toast } from "react-toastify";
import { Pose } from "@mediapipe/pose";

// Hooks
import { useCamera } from "../hooks/useCamera";

// Helpers
import { newImage } from "../helpers/canvasHelper";

// Assets
import gameImg from "../assets/game/sprite/flappybird.png";

export const LEVELS = {
  1: {
    duration: 120,
    starSpeed: 200,
    starSpawnRate: 200,
  },
  2: {
    duration: 120,
    starSpeed: 220,
    starSpawnRate: 180,
  },
  3: {
    duration: 120,
    starSpeed: 240,
    starSpawnRate: 160,
  },
  4: {
    duration: 120,
    starSpeed: 260,
    starSpawnRate: 140,
  },
  5: {
    duration: 120,
    starSpeed: 280,
    starSpawnRate: 120,
  },
};

export default function FlappyBird({
  level,
  start,
  gameOver,
  setScore,
  setView,
  setStart,
}) {
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  const videoStart = React.useRef(false);
  const poseStart = React.useRef(false);
  const workerStart = React.useRef(false);

  const [canvasSize, setCanvasSize] = React.useState({ w: 0, h: 0 });

  const { camStart, setCamStart, camError } = useCamera(videoRef);

  const gameWorker = React.useMemo(
    () =>
      new Worker(new URL("../workers/flappybirdWorker.js", import.meta.url)),
    []
  );

  // Game Over
  React.useEffect(() => {
    // Handle Game Over
    if (gameOver) {
      // Stop Camera
      setCamStart((prev) => {
        if (prev) {
          return false;
        }
        return false;
      });

      // Signal Camera Stop
      videoStart.current = false;

      // Stop Pose Worker
      gameWorker.terminate();

      // Signal Pose Worker Stop
      workerStart.current = false;

      // Signal Pose Estimation Stop
      poseStart.current = false;

      // Redirect to Game End page
      if (!camStart && !poseStart.current) {
        setView("end");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camStart, gameOver]);

  // Camera Video
  React.useEffect(() => {
    const videoEle = videoRef.current;

    // Alert if Camera Video is not available
    if (camError !== "") {
      toast.warn("Please allow camera access and reload page");
    }

    if (!camStart) {
      setCamStart((prev) => {
        if (prev) {
          return prev;
        }
        return true;
      });
    }

    // On Loaded Data
    videoEle.onloadeddata = () => {
      // Signal Camera Start
      videoStart.current = true;

      // Signal Pose Estimation Start
      poseStart.current = true;
    };
  });

  // Game Worker
  React.useEffect(() => {
    const canvasEle = canvasRef.current;

    setCanvasSize(() => {
      return {
        w: canvasEle.clientWidth,
        h: canvasEle.clientHeight,
      };
    });

    // Start Game Worker
    if (!workerStart.current && !gameOver && canvasSize.w !== 0) {
      const canvasOff = canvasEle.transferControlToOffscreen();

      newImage(gameImg).then((gameSprite) => {
        toast.info("Game Initializing...", {
          toastId: "gameToast",
          autoClose: false,
        });

        gameWorker.postMessage(
          {
            msg: "init",
            level: LEVELS[level],
            canvas: canvasOff,
            sprite: gameSprite,
          },
          [canvasOff]
        );
      });

      // Signal Worker Start
      workerStart.current = true;

      // On Message
      gameWorker.onmessage = (e) => {
        switch (e.data.msg) {
          case "started":
            if (e.data.started) {
              setStart(true);
            }
            break;
          case "initialized":
            if (e.data.initialized) {
              toast.update("gameToast", {
                render:
                  "Game Initialized...\n\nStand & Squat to collect all the stars. The more stars you collect the higher your score!\n\nCollect the coin to start the game",
                type: toast.TYPE.SUCCESS,
                autoClose: 10000,
              });
            }
            break;
          case "score":
            setScore(e.data.score);
            break;
          default:
            break;
        }
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camStart, gameOver, gameWorker]);

  // Pose Model
  React.useEffect(() => {
    const videoEle = videoRef.current;

    const poseModel = new Pose({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
      },
    });

    poseModel.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      selfieMode: true,
    });

    poseModel.onResults((results) => {
      if (results.poseLandmarks) {
        if (!poseStart.current) {
          poseStart.current = true;
        }
      } else {
        toast.warn("Stay in a well-lit area and Stand in front of the camera", {
          toastId: "poseToast",
          autoClose: 5000,
        });
      }

      // Send Pose Results to Game Worker
      if (!start) {
        createImageBitmap(results.image).then((img) => {
          gameWorker.postMessage(
            {
              msg: "pose",
              poseImage: img,
              poseLandmarks: results.poseLandmarks,
            },
            [img]
          );
        });
      } else {
        gameWorker.postMessage({
          msg: "pose",
          poseLandmarks: results.poseLandmarks,
        });
      }
    });

    const sendToPoseEst = async () => {
      if (videoStart.current) {
        await poseModel.send({ image: videoEle });
      }

      if (!gameOver) {
        requestAnimationFrame(sendToPoseEst);
      } else {
        poseModel.close();
        poseStart.current = false;
      }
    };

    // Start Pose Estimation if Camera Video is available
    if (camStart) {
      sendToPoseEst();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camStart]);

  return (
    <div className="bg-black w-full h-full relative">
      <div className="absolute bg-black/70 w-full h-full z-20">
        <div className="flex flex-col items-center justify-center w-full h-full">
          <canvas
            ref={canvasRef}
            width={canvasSize.w}
            height={canvasSize.h}
            className="h-full w-full"
          />
        </div>
      </div>

      <div
        className={`bottom-0 left-0 right-0 mx-auto w-full h-[200px] sm:h-full`}
      >
        <div className="relative h-full">
          <video
            ref={videoRef}
            autoPlay
            className={`absolute hidden bg-slate-500 top-0 left-0 right-0 mx-auto h-[200px] sm:h-full scale-x-[-1]`}
          />
        </div>
      </div>
    </div>
  );
}
